import { Skeleton, Stack, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import Iconify from 'src/components/iconify';
import FallbackUI from './fallback-ui';
import { CandidateProfileContext } from './context/candidate-profile-context';

const useStyles = makeStyles(theme => ({
  mainDiv: {
    gap: 24,
  },
  qualifiedDiv: {
    padding: '11.5px 16px',
    background: theme.palette.content.primaryGreen2,
    color: theme.palette.background.primaryGreen1,
  },
  disqualifiedDiv: {
    padding: '11.5px 16px',
    background: theme.palette.content.primaryRed2,
    color: theme.palette.background.primaryRed1,
  },
  screeningItemDiv: {
    borderRadius: 6,
    padding: 20,
    border: `1px solid ${theme.palette.border.primaryGreen1}`,
  },
  screeningItemDivError: {
    borderRadius: 6,
    padding: 20,
    border: `1px solid ${theme.palette.border.primaryRed1}`,
  },
  qualifiedIndicatorDiv: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    left: '89%',
    gap: 6,
    padding: '6px 8px',
    marginTop: -10,
    width: 90,
    borderRadius: 22,
    background: theme.palette.content.primaryGreen2,
    color: theme.palette.background.primaryGreen1,
    [theme.breakpoints.down('lg')]: {
      left: '85%',
    },
    [theme.breakpoints.down('md')]: {
      left: '80%',
    },
  },
  disqualifiedIndicatorDiv: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    left: '87%',
    gap: 6,
    padding: '6px 8px',
    marginTop: -10,
    width: 112,
    borderRadius: 22,
    background: theme.palette.content.primaryRed2,
    color: theme.palette.background.primaryRed1,
    [theme.breakpoints.down('lg')]: {
      left: '82%',
    },
    [theme.breakpoints.down('md')]: {
      left: '80%',
    },
  },
  itemFirstDiv: {
    gap: '14px',
    marginBottom: '20px',
    marginTop: '-16px',
  },
  firstDivText: {
    fontWeight: 600,
    margin: 0,
  },
  tickIcon: {
    color: theme.palette.background.primaryGreen1,
  },
  crossIcon: {
    color: theme.palette.background.primaryRed1,
  },
  remarkText: {
    lineHeight: 'normal',
    color: theme.palette.content.primaryBlack3,
  },
}));

const QualifiedTag = ({ success, forCriteria }) => {
  const classes = useStyles();
  return (
    <Stack
      className={
        success
          ? classes.qualifiedIndicatorDiv
          : classes.disqualifiedIndicatorDiv
      }
    >
      {success ? (
        <Iconify
          width={16}
          height={16}
          className={classes.tickIcon}
          icon="tabler:circle-check-filled"
        />
      ) : (
        <Iconify
          width={16}
          height={16}
          className={classes.crossIcon}
          icon="tabler:circle-x-filled"
        />
      )}
      {!forCriteria && (
        <Typography variant="caption" sx={{ fontSize: '12px' }}>
          {success ? 'Qualified' : 'Disqualified'}
        </Typography>
      )}
      {forCriteria && (
        <Typography variant="caption" sx={{ fontSize: '12px' }}>
          {success ? 'Matching' : 'Not Matching'}
        </Typography>
      )}
    </Stack>
  );
};

QualifiedTag.propTypes = {
  success: PropTypes.bool.isRequired,
  forCriteria: PropTypes.bool.isRequired,
};

const ScreeningGroup = ({ jobId }) => {
  // hooks
  const classes = useStyles();
  const { obj: candidateDetails, isLoading } = useContext(
    CandidateProfileContext,
  );

  // variables
  const skillMatch = candidateDetails?.skill_match;
  const criteriaMatch = candidateDetails?.criteria_match;

  console.log('criteria ', criteriaMatch);

  // components
  const ScreeningGroupSkeleton = (
    <Stack spacing="24px" mb="22px">
      <Skeleton
        variant="rectangular"
        width="100%"
        height={48}
        sx={{
          backgroundColor: '#FAFAFA',
        }}
      />
      <Stack spacing="22px">
        <Skeleton width={216} height={10} />
        <Stack
          sx={{
            border: theme => `1px solid ${theme.palette.border.primaryBlack3}`,
            padding: '20px',
            height: '187px',
            position: 'relative',
            justifyContent: 'space-between',
          }}
        >
          <Skeleton
            width={90}
            height={28}
            sx={{
              position: 'absolute',
              right: '12px',
              top: '12px',
              borderRadius: '22px',
            }}
          />
          <Stack
            sx={{
              gap: '12px',
            }}
          >
            <Skeleton width={94} height={9} />
            <Skeleton width={51} height={8} />
          </Stack>
          <Stack spacing="23px">
            <Skeleton width={130} height={9} />
            <Stack spacing="11px">
              <Skeleton width="100%" height={9} />
              <Skeleton width={260} height={9} />
            </Stack>
          </Stack>
        </Stack>
      </Stack>
      <Stack spacing="22px">
        <Skeleton width={216} height={10} />
        <Stack spacing="12px">
          <Stack
            sx={{
              border: theme =>
                `1px solid ${theme.palette.border.primaryBlack3}`,
              padding: '20px',
              height: '187px',
              position: 'relative',
              justifyContent: 'space-between',
            }}
          >
            <Skeleton
              width={90}
              height={28}
              sx={{
                position: 'absolute',
                right: '12px',
                top: '12px',
                borderRadius: '22px',
              }}
            />
            <Stack
              sx={{
                gap: '12px',
              }}
            >
              <Skeleton width={94} height={9} />
              <Skeleton width={51} height={8} />
            </Stack>
            <Stack spacing="23px">
              <Skeleton width={130} height={9} />
              <Stack spacing="11px">
                <Skeleton width="100%" height={9} />
                <Skeleton width={260} height={9} />
              </Stack>
            </Stack>
          </Stack>
          <Stack
            sx={{
              border: theme =>
                `1px solid ${theme.palette.border.primaryBlack3}`,
              padding: '20px',
              height: '187px',
              position: 'relative',
              justifyContent: 'space-between',
            }}
          >
            <Skeleton
              width={90}
              height={28}
              sx={{
                position: 'absolute',
                right: '12px',
                top: '12px',
                borderRadius: '22px',
              }}
            />
            <Stack
              sx={{
                gap: '12px',
              }}
            >
              <Skeleton width={94} height={9} />
              <Skeleton width={51} height={8} />
            </Stack>
            <Stack spacing="23px">
              <Skeleton width={130} height={9} />
              <Stack spacing="11px">
                <Skeleton width="100%" height={9} />
                <Skeleton width={260} height={9} />
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );

  return isLoading ? (
    <Stack
      width="100%"
      height="100%"
      justifyContent="center"
      alignItems="center"
    >
      Loading...
    </Stack>
  ) : (
    <Stack className={classes.mainDiv}>
      {skillMatch ? (
        <>
          <Stack
            className={
              skillMatch?.selected === true
                ? classes.qualifiedDiv
                : classes.disqualifiedDiv
            }
          >
            <Typography variant="subheading1">
              {skillMatch?.selected === true ? 'Qualified' : 'Disqualified'}
            </Typography>
          </Stack>

          <Stack spacing="16px">
            <Typography variant="subheading2">Qualifying Criteria</Typography>
            <Stack
              className={
                skillMatch?.selected
                  ? classes.screeningItemDiv
                  : classes.screeningItemDivError
              }
            >
              <QualifiedTag
                success={skillMatch?.selected}
                forCriteria={false}
              />
              <Stack className={classes.itemFirstDiv}>
                <Typography
                  variant="paragraph"
                  className={classes.firstDivText}
                >
                  Primary Skill
                </Typography>
                {skillMatch?.primary_skill && (
                  <Typography variant="input" sx={{ fontWeight: 600 }}>
                    {skillMatch?.primary_skill}
                  </Typography>
                )}
              </Stack>
              <Stack spacing="10px">
                <Typography varaint="subtitle1">Remarks</Typography>
                <Typography variant="input" className={classes.remarkText}>
                  {skillMatch?.match_remark}
                </Typography>
              </Stack>
            </Stack>
          </Stack>

          {skillMatch?.selected === true && criteriaMatch !== null && (
            <Stack spacing="16px">
              <Typography variant="subheading2">Preferred Criteria</Typography>
              {Object.values(criteriaMatch?.criteria_wise_scores)?.map(
                (item, index) => (
                  <Stack
                    key={index}
                    className={
                      item?.score > 5.0
                        ? classes.screeningItemDiv
                        : classes.screeningItemDivError
                    }
                  >
                    <QualifiedTag success={item?.score > 5.0} forCriteria />
                    <Stack className={classes.itemFirstDiv}>
                      <Typography
                        variant="paragraph"
                        className={classes.firstDivText}
                      >
                        Criteria {index + 1}
                      </Typography>
                      {item?.name && (
                        <Typography variant="input" sx={{ fontWeight: 600 }}>
                          {item?.name}
                        </Typography>
                      )}
                    </Stack>
                    <Stack spacing="10px">
                      <Typography varaint="subtitle1">Remarks</Typography>
                      <Typography
                        variant="input"
                        className={classes.remarkText}
                      >
                        {item?.remark}
                      </Typography>
                    </Stack>
                  </Stack>
                ),
              )}
            </Stack>
          )}
        </>
      ) : (
        <FallbackUI content="No data found" />
      )}
    </Stack>
  );
};

export default ScreeningGroup;

ScreeningGroup.propTypes = {
  jobId: PropTypes.string.isRequired,
};
